<template>
  <div class="tab-content my-products-page">
    <div class="main-header products-header">
      <div>
        <h1>{{ $t('controlCenter.myProducts.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.myProducts.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="fetchProducts" :disabled="loading">
        <i class="fas fa-rotate-right"></i>
        {{ $t('controlCenter.common.refresh') }}
      </button>
    </div>

    <div class="product-overview-grid">
      <div class="overview-panel hero-panel">
        <span>{{ $t('controlCenter.myProducts.stats.active') }}</span>
        <strong>{{ activeProducts.length }}</strong>
        <p>{{ nextExpiryText }}</p>
      </div>
      <div class="overview-panel">
        <span>{{ $t('controlCenter.myProducts.stats.expired') }}</span>
        <strong>{{ expiredProducts.length }}</strong>
        <p>{{ $t('controlCenter.myProducts.stats.total', { count: products.length }) }}</p>
      </div>
    </div>

    <section
      v-for="group in productGroups"
      :key="group.key"
      class="product-section"
    >
      <div class="section-heading group-heading">
        <div>
          <span class="section-kicker">{{ group.kicker }}</span>
          <h2>{{ group.title }}</h2>
        </div>
        <div class="section-heading-actions">
          <button
            v-if="group.key === 'licence' && group.active.length"
            class="create-auditor-link"
            @click="goCreateAuditor"
          >
            <i class="fas fa-user-plus"></i>
            <span>{{ $t('controlCenter.myProducts.actions.createAuditor') }}</span>
          </button>
          <span class="section-count">{{ group.items.length }}</span>
        </div>
      </div>

      <div class="group-block">
        <div class="group-subheading">
          <span>{{ $t('controlCenter.myProducts.activeTitle') }}</span>
          <strong>{{ group.active.length }}</strong>
        </div>

        <div v-if="loading" class="loading-panel">
          <i class="fas fa-circle-notch fa-spin"></i>
          <span>{{ $t('controlCenter.common.loading') }}</span>
        </div>
        <div v-else-if="!group.active.length" class="empty-panel">
          <i class="fas fa-box"></i>
          <strong>{{ group.emptyActiveTitle }}</strong>
          <span>{{ group.emptyActiveText }}</span>
        </div>
        <div v-else class="product-card-grid">
          <article
            v-for="product in group.active"
            :key="productKey(product)"
            class="product-card active-card"
          >
            <div class="card-glow"></div>
            <div class="product-card-top">
              <div class="product-icon">
                <i :class="productIcon(product)"></i>
              </div>
              <span class="status-pill active-pill">{{ $t('controlCenter.myProducts.status.active') }}</span>
            </div>
            <h3>{{ productName(product) }}</h3>
            <p>{{ productDescription(product) }}</p>
            <div class="validity-strip">
              <span>{{ $t('controlCenter.myProducts.expiresAt') }}</span>
              <strong>{{ formatDate(product.expiresAt) }}</strong>
            </div>
            <div class="product-meta">
              <div>
                <span>{{ $t('controlCenter.myProducts.startsAt') }}</span>
                <strong>{{ formatDate(product.startsAt) }}</strong>
              </div>
            </div>
            <div v-if="productTags(product).length" class="tag-row">
              <span v-for="tag in productTags(product)" :key="tag" class="soft-tag">{{ tag }}</span>
            </div>
          </article>
        </div>
      </div>

      <div class="group-block expired-section">
        <button
          class="group-subheading muted-subheading expired-toggle"
          type="button"
          :aria-expanded="String(!isExpiredCollapsed(group.key))"
          @click="toggleExpiredSection(group.key)"
        >
          <span>
            <i
              class="fas fa-chevron-right expired-toggle-icon"
              :class="{ expanded: !isExpiredCollapsed(group.key) }"
            ></i>
            {{ $t('controlCenter.myProducts.expiredTitle') }}
          </span>
          <strong>{{ group.expired.length }}</strong>
        </button>

        <div v-show="!isExpiredCollapsed(group.key)" class="expired-content">
          <div v-if="loading" class="loading-panel compact">
            <i class="fas fa-circle-notch fa-spin"></i>
            <span>{{ $t('controlCenter.common.loading') }}</span>
          </div>
          <div v-else-if="!group.expired.length" class="empty-panel compact">
            <i class="fas fa-check-circle"></i>
            <strong>{{ group.emptyExpiredTitle }}</strong>
            <span>{{ group.emptyExpiredText }}</span>
          </div>
          <div v-else class="expired-list">
            <div
              v-for="product in group.expired"
              :key="productKey(product)"
              class="expired-row"
            >
              <div class="expired-icon">
                <i :class="productIcon(product)"></i>
              </div>
              <div class="expired-main">
                <strong>{{ productName(product) }}</strong>
                <span>{{ productDescription(product) }}</span>
              </div>
              <div class="expired-period">
                <div>
                  <span>{{ $t('controlCenter.myProducts.startsAt') }}</span>
                  <strong>{{ formatDate(product.startsAt) }}</strong>
                </div>
                <div>
                  <span>{{ $t('controlCenter.myProducts.expiredAt') }}</span>
                  <strong>{{ formatDate(product.expiresAt) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'

export default {
  name: 'MyProducts',
  data() {
    return {
      loading: false,
      products: [],
      planOptions: {},
      languageOptions: {},
      standardOptions: {},
      expiredCollapsed: {
        package: true,
        language: true,
        standard: true,
        licence: true
      }
    }
  },
  computed: {
    activeProducts() {
      return this.products
        .filter(item => !this.isExpired(item))
        .sort((a, b) => this.dateValue(a.expiresAt) - this.dateValue(b.expiresAt))
    },
    expiredProducts() {
      return this.products
        .filter(item => this.isExpired(item))
        .sort((a, b) => this.dateValue(b.expiresAt) - this.dateValue(a.expiresAt))
    },
    productGroups() {
      return [
        this.buildProductGroup('package', product => product.kind === 'PLAN'),
        this.buildProductGroup('language', product => product.itemType === 'LANGUAGE'),
        this.buildProductGroup('standard', product => product.itemType === 'STANDARD'),
        this.buildProductGroup('licence', product => this.isLicenceProduct(product))
      ]
    },
    nextExpiryText() {
      const next = this.activeProducts.find(item => item.expiresAt)
      if (!next) return this.$t('controlCenter.myProducts.stats.noExpiry')
      return this.$t('controlCenter.myProducts.stats.nextExpiry', {
        product: this.productName(next),
        date: this.formatDate(next.expiresAt)
      })
    }
  },
  created() {
    this.fetchProducts()
  },
  methods: {
    async fetchProducts() {
      this.loading = true
      try {
        const [productsResponse, configResponse] = await Promise.all([
          paymentAdminApi.getMyProducts(),
          paymentAdminApi.getPurchaseConfig()
        ])
        const productData = productsResponse && productsResponse.data ? productsResponse.data : productsResponse
        const configData = configResponse && configResponse.data ? configResponse.data : configResponse
        this.products = this.normalizeProducts(productData)
        this.planOptions = configData.plans || {}
        this.languageOptions = (configData.languages || []).reduce((map, item) => {
          map[item.id] = item.name
          return map
        }, {})
        this.standardOptions = (configData.standards || []).reduce((map, item) => {
          map[item.id] = item.name
          return map
        }, {})
      } catch (error) {
        console.error('Failed to fetch my products:', error)
        this.$message.error(this.$t('controlCenter.myProducts.loadError'))
      } finally {
        this.loading = false
      }
    },
    goCreateAuditor() {
      this.$router.push({ path: '/controlCenter', query: { tab: 'users' } })
    },
    isExpiredCollapsed(groupKey) {
      return this.expiredCollapsed[groupKey] !== false
    },
    toggleExpiredSection(groupKey) {
      this.$set(this.expiredCollapsed, groupKey, !this.isExpiredCollapsed(groupKey))
    },
    buildProductGroup(groupKey, matcher) {
      const items = this.products.filter(matcher)
      return {
        key: groupKey,
        title: this.$t(`controlCenter.myProducts.groups.${groupKey}.title`),
        kicker: this.$t(`controlCenter.myProducts.groups.${groupKey}.kicker`),
        emptyActiveTitle: this.$t(`controlCenter.myProducts.groups.${groupKey}.emptyActiveTitle`),
        emptyActiveText: this.$t(`controlCenter.myProducts.groups.${groupKey}.emptyActiveText`),
        emptyExpiredTitle: this.$t(`controlCenter.myProducts.groups.${groupKey}.emptyExpiredTitle`),
        emptyExpiredText: this.$t(`controlCenter.myProducts.groups.${groupKey}.emptyExpiredText`),
        items,
        active: items
          .filter(item => !this.isExpired(item))
          .sort((a, b) => this.dateValue(a.expiresAt) - this.dateValue(b.expiresAt)),
        expired: items
          .filter(item => this.isExpired(item))
          .sort((a, b) => this.dateValue(b.expiresAt) - this.dateValue(a.expiresAt))
      }
    },
    normalizeProducts(data) {
      const rows = Array.isArray(data) ? data : (data && Array.isArray(data.products) ? data.products : [])
      const productMap = rows
        .filter(this.isSupportedProduct)
        .reduce((map, product) => {
          const key = this.productIdentity(product)
          if (!key) return map
          map[key] = map[key] ? this.mergeProduct(map[key], product) : { ...product }
          return map
        }, {})
      return Object.values(productMap)
    },
    productKey(product) {
      return this.productIdentity(product) || [product.kind, product.itemType, product.itemCode, product.planId].filter(Boolean).join('-')
    },
    productIdentity(product) {
      const itemType = product.itemType || (product.kind === 'PLAN' ? 'PLAN' : '')
      const normalizedItemType = this.isLicenceProduct(product) ? 'LICENCE' : itemType
      const itemCode = product.kind === 'PLAN' ? product.planId : (this.isLicenceProduct(product) ? 'licence' : product.itemCode)
      if (!itemType || !itemCode) return ''
      return `${normalizedItemType}:${itemCode}`
    },
    isSupportedProduct(product) {
      if (!product) return false
      if (product.kind === 'PLAN') return Boolean(product.planId || product.itemCode)
      return ['LANGUAGE', 'STANDARD', 'LICENCE', 'AUDITOR_SEAT', 'ADMIN_SEAT'].includes(product.itemType)
    },
    isLicenceProduct(product) {
      return product && ['LICENCE', 'AUDITOR_SEAT', 'ADMIN_SEAT'].includes(product.itemType)
    },
    licenceQuantity(product) {
      const quantity = Number(product && product.quantity)
      const auditorSeats = Number(product && product.auditorSeats)
      const adminSeats = Number(product && product.adminSeats)
      if (Number.isFinite(quantity) && quantity > 0) return quantity
      return (Number.isFinite(auditorSeats) ? auditorSeats : 0) + (Number.isFinite(adminSeats) ? adminSeats : 0) || 1
    },
    mergeProduct(current, incoming) {
      const startsAt = this.earlierDate(current.startsAt, incoming.startsAt)
      const duration = this.productDuration(current) + this.productDuration(incoming)
      const latestUpdatedAt = this.laterDate(current.updatedAt, incoming.updatedAt)
      const latest = latestUpdatedAt === incoming.updatedAt ? incoming : current
      const isLicence = this.isLicenceProduct(current) || this.isLicenceProduct(incoming)
      return {
        ...current,
        itemType: isLicence ? 'LICENCE' : current.itemType,
        itemCode: isLicence ? 'licence' : current.itemCode,
        name: isLicence ? null : current.name,
        orderNo: latest.orderNo,
        sourceOrderNo: latest.sourceOrderNo,
        status: latest.status || current.status,
        quantity: isLicence ? this.licenceQuantity(current) + this.licenceQuantity(incoming) : (latest.quantity || current.quantity),
        auditorSeats: isLicence ? null : (latest.auditorSeats || current.auditorSeats),
        adminSeats: isLicence ? null : (latest.adminSeats || current.adminSeats),
        startsAt,
        expiresAt: isLicence ? this.laterDate(current.expiresAt, incoming.expiresAt) : this.addDuration(startsAt, duration),
        createdAt: this.earlierDate(current.createdAt, incoming.createdAt),
        updatedAt: latestUpdatedAt
      }
    },
    productDuration(product) {
      const start = this.dateTimestamp(product.startsAt)
      const end = this.dateTimestamp(product.expiresAt)
      return start === null || end === null || end < start ? 0 : end - start
    },
    addDuration(value, duration) {
      const timestamp = this.dateTimestamp(value)
      if (timestamp === null) return null
      return new Date(timestamp + duration).toISOString()
    },
    earlierDate(first, second) {
      if (!first) return second || null
      if (!second) return first
      return this.dateTimestamp(first) <= this.dateTimestamp(second) ? first : second
    },
    laterDate(first, second) {
      if (!first) return second || null
      if (!second) return first
      return this.dateTimestamp(first) >= this.dateTimestamp(second) ? first : second
    },
    dateTimestamp(value) {
      if (!value) return null
      const timestamp = new Date(value).getTime()
      return Number.isNaN(timestamp) ? null : timestamp
    },
    productName(product) {
      if (this.isLicenceProduct(product)) return this.$t('controlCenter.myProducts.types.licence')
      if (product.name) return product.name
      if (product.kind === 'PLAN') return this.planLabel(product.planId || product.itemCode)
      if (product.itemType === 'LANGUAGE') return this.languageLabel(product.itemCode)
      if (product.itemType === 'STANDARD') return this.standardLabel(product.itemCode)
      return product.itemCode || product.planId || this.$t('controlCenter.common.unknown')
    },
    productDescription(product) {
      if (product.description) return product.description
      if (product.kind === 'PLAN') {
        return this.$t('controlCenter.myProducts.descriptions.plan', {
          cycle: this.billingCycleLabel(product.billingCycle)
        })
      }
      if (product.itemType === 'LANGUAGE') return this.$t('controlCenter.myProducts.descriptions.language')
      if (product.itemType === 'STANDARD') return this.$t('controlCenter.myProducts.descriptions.standard')
      if (this.isLicenceProduct(product)) {
        return this.$t('controlCenter.myProducts.descriptions.seat', {
          count: this.licenceQuantity(product)
        })
      }
      return this.$t('controlCenter.myProducts.descriptions.addOn')
    },
    productIcon(product) {
      if (product.kind === 'PLAN') return 'fas fa-layer-group'
      if (product.itemType === 'LANGUAGE') return 'fas fa-language'
      if (product.itemType === 'STANDARD') return 'fas fa-file-shield'
      if (this.isLicenceProduct(product)) return 'fas fa-user-check'
      return 'fas fa-cube'
    },
    productTags(product) {
      const tags = []
      if (product.billingCycle) tags.push(this.billingCycleLabel(product.billingCycle))
      if (product.kind !== 'PLAN' && this.isLicenceProduct(product)) {
        tags.push(this.$t('controlCenter.myProducts.quantity', { count: this.licenceQuantity(product) }))
      } else if (product.kind !== 'PLAN' && product.quantity) {
        tags.push(this.$t('controlCenter.myProducts.quantity', { count: product.quantity }))
      }
      if (product.kind !== 'PLAN' && Array.isArray(product.languages)) {
        product.languages.slice(0, 3).forEach(item => tags.push(this.languageLabel(item)))
      }
      if (product.kind !== 'PLAN' && Array.isArray(product.standards)) {
        product.standards.slice(0, 3).forEach(item => tags.push(this.standardLabel(item)))
      }
      return tags
    },
    planLabel(planId) {
      const plan = this.planOptions[planId]
      if (plan && plan.name) return plan.name
      const key = `controlCenter.auditDashboard.plans.${planId}`
      const text = this.$t(key)
      return text === key ? planId || '-' : text
    },
    languageLabel(languageId) {
      if (this.languageOptions[languageId]) return this.languageOptions[languageId]
      const key = `buy.languages.items.${languageId}`
      const text = this.$t(key)
      return text === key ? languageId || '-' : text
    },
    standardLabel(standardId) {
      if (this.standardOptions[standardId]) return this.standardOptions[standardId]
      const key = `buy.standards.items.${standardId}.fullname`
      const text = this.$t(key)
      return text === key ? standardId || '-' : text
    },
    billingCycleLabel(value) {
      const key = `controlCenter.billingCycleLabels.${value || 'one_year'}`
      const text = this.$t(key)
      return text === key ? value || '-' : text
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 10)
    },
    dateValue(value) {
      if (!value) return Number.MAX_SAFE_INTEGER
      const date = new Date(value).getTime()
      return Number.isNaN(date) ? Number.MAX_SAFE_INTEGER : date
    },
    isExpired(product) {
      const value = this.dateValue(product.expiresAt)
      return value !== Number.MAX_SAFE_INTEGER && value < Date.now()
    }
  }
}
</script>

<style scoped>
.my-products-page {
  --product-accent: #2563eb;
  --product-accent-soft: #dbeafe;
  --product-border: #e2e8f0;
  --product-muted: #64748b;
  --product-surface: #ffffff;
  --product-bg: #f8fafc;
}

.products-header {
  align-items: flex-start;
}

.product-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(240px, 0.7fr);
  gap: 18px;
  margin-bottom: 24px;
}

.overview-panel {
  padding: 22px 24px;
  border-radius: 8px;
  background: var(--product-surface);
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow:
    0 18px 40px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.hero-panel {
  background:
    linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(255, 255, 255, 0.9)),
    var(--product-surface);
  border-color: rgba(37, 99, 235, 0.22);
}

.overview-panel span {
  display: block;
  color: var(--product-muted);
  font-size: 13px;
  margin-bottom: 10px;
}

.overview-panel strong {
  display: block;
  color: #0f172a;
  font-size: 34px;
  line-height: 1;
  margin-bottom: 10px;
}

.overview-panel p {
  margin: 0;
  color: #475569;
  font-size: 14px;
}

.product-section {
  margin-top: 30px;
  padding-top: 2px;
}

.product-section + .product-section {
  padding-top: 28px;
  border-top: 1px solid rgba(148, 163, 184, 0.22);
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-heading-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.create-auditor-link {
  height: 34px;
  padding: 0 13px;
  border: 1px solid rgba(37, 99, 235, 0.24);
  border-radius: 8px;
  background: #eff6ff;
  color: var(--product-accent);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-auditor-link:hover {
  background: var(--product-accent);
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.2);
}

.section-kicker {
  display: block;
  color: var(--product-accent);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.section-heading h2 {
  margin: 0;
  color: #0f172a;
  font-size: 22px;
  font-weight: 700;
}

.group-heading {
  margin-bottom: 18px;
}

.group-block + .group-block {
  margin-top: 20px;
}

.group-subheading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #0f172a;
}

.group-subheading span {
  font-size: 14px;
  font-weight: 700;
}

.group-subheading strong {
  min-width: 30px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--product-accent-soft);
  color: var(--product-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.muted-subheading strong {
  background: #f1f5f9;
  color: #64748b;
}

.expired-toggle {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.expired-toggle span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.expired-toggle-icon {
  color: #94a3b8;
  font-size: 11px;
  transition: transform 0.18s ease;
}

.expired-toggle-icon.expanded {
  transform: rotate(90deg);
}

.expired-content {
  margin-top: 12px;
}

.section-count {
  min-width: 42px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--product-accent-soft);
  color: var(--product-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.muted-count {
  background: #f1f5f9;
  color: #64748b;
}

.product-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.product-card {
  position: relative;
  overflow: hidden;
  min-height: 280px;
  padding: 20px;
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(248, 250, 252, 0.98)),
    var(--product-surface);
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow:
    0 22px 38px rgba(15, 23, 42, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -1px 0 rgba(15, 23, 42, 0.04);
}

.card-glow {
  position: absolute;
  top: -60px;
  right: -40px;
  width: 170px;
  height: 170px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.18), rgba(37, 99, 235, 0));
  pointer-events: none;
}

.product-card-top {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.product-icon,
.expired-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #eff6ff;
  color: var(--product-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.status-pill {
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
}

.active-pill {
  color: #0369a1;
  background: #e0f2fe;
}

.product-card h3 {
  position: relative;
  margin: 0 0 8px;
  color: #0f172a;
  font-size: 20px;
  line-height: 1.25;
}

.product-card p {
  position: relative;
  margin: 0 0 20px;
  color: var(--product-muted);
  line-height: 1.55;
  min-height: 44px;
}

.validity-strip {
  position: relative;
  padding: 14px 16px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.validity-strip span,
.product-meta span,
.expired-period span {
  color: var(--product-muted);
  font-size: 12px;
}

.validity-strip strong {
  color: #0f172a;
  font-size: 16px;
}

.product-meta {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}

.product-meta div {
  min-width: 0;
}

.product-meta strong {
  display: block;
  margin-top: 4px;
  color: #1e293b;
  font-size: 13px;
  word-break: break-word;
}

.tag-row {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.soft-tag {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: #eef2ff;
  color: #334155;
  font-size: 12px;
}

.loading-panel,
.empty-panel {
  min-height: 180px;
  border-radius: 8px;
  border: 1px dashed var(--product-border);
  background: var(--product-surface);
  color: var(--product-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.loading-panel.compact,
.empty-panel.compact {
  min-height: 120px;
}

.empty-panel i,
.loading-panel i {
  color: var(--product-accent);
  font-size: 24px;
}

.empty-panel strong {
  color: #0f172a;
  font-size: 16px;
}

.expired-list {
  border-radius: 8px;
  border: 1px solid var(--product-border);
  background: var(--product-surface);
  overflow: hidden;
}

.expired-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid #edf2f7;
  opacity: 0.78;
}

.expired-row:last-child {
  border-bottom: none;
}

.expired-icon {
  width: 40px;
  height: 40px;
  color: #64748b;
  background: #f1f5f9;
  font-size: 16px;
}

.expired-main {
  min-width: 0;
}

.expired-main strong,
.expired-main span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expired-main strong {
  color: #334155;
  margin-bottom: 4px;
}

.expired-main span {
  color: var(--product-muted);
  font-size: 13px;
}

.expired-period {
  display: grid;
  grid-template-columns: repeat(2, minmax(110px, auto));
  gap: 16px;
  text-align: right;
}

.expired-period strong {
  display: block;
  color: #334155;
  margin-top: 4px;
}

@media (max-width: 1200px) {
  .product-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .product-overview-grid,
  .product-card-grid {
    grid-template-columns: 1fr;
  }

  .expired-row {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .expired-period {
    grid-column: 2;
    text-align: left;
  }
}

@media (max-width: 520px) {
  .product-meta {
    grid-template-columns: 1fr;
  }
}
</style>
